import React, { useMemo } from 'react'

import { tx } from '@twind/core'

type ButtonGroupOption = {
  key: string
  label: string
  count?: string | number
  background?: string
}

type ButtonGroupProps<T extends ButtonGroupOption> = {
  options: T[]
  selected?: string[]
  multi?: boolean
  onChangeValue?: (_value: string[]) => void
  selectedClassName?: string
  unSelectedClassName?: string
  itemClassName?: string | ((_value: T, _index: number) => string)
  className?: string
}

const ButtonGroup = <T extends ButtonGroupOption>(
  props: ButtonGroupProps<T>
) => {
  const {
    options,
    selected = [],
    onChangeValue,
    multi = false,
    unSelectedClassName = '',
    selectedClassName = '',
    itemClassName = '',
    className = ''
  } = props
  const total = useMemo(() => options.length, [options])

  const handleOnClick = (key: string, isSelected: boolean) => {
    if (!onChangeValue) return

    if (multi) {
      if (isSelected) onChangeValue(selected.filter((id) => id !== key))
      else onChangeValue([...selected, key])
    } else {
      onChangeValue([key])
    }
  }

  return (
    <div
      className={tx(
        'flex shadow-sm rounded-md border border-zinc-600',
        className
      )}>
      {options.map((option, index) => {
        const isLast = index === total - 1
        const borderRadius =
          index === 0 ? 'rounded-l' : isLast ? 'rounded-r' : ''
        const border = isLast ? '' : 'border-r border-zinc-600'
        const isSelected = selected.includes(option.key)

        return (
          <button
            key={option.key}
            onClick={() => handleOnClick(option.key, isSelected)}
            className={tx(
              'relative px-4 py-2 font-medium transition-colors focus:outline-none focus:z-10 focus:ring text-center',
              isSelected
                ? selectedClassName ||
                    `${
                      option.background
                        ? `${option.background} text-content `
                        : 'bg-zinc-700'
                    }`
                : unSelectedClassName || 'bg-zinc-900',
              borderRadius,
              border,
              typeof itemClassName === 'string'
                ? itemClassName
                : itemClassName(option, index)
            )}>
            <span>{option.label}</span>
            {option.count && (
              <span className="ml-1 rounded-full px-2 py-0.5 text-sm bg-indigo-500">
                {option.count}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}

export default ButtonGroup
